import React, { useEffect, useState } from 'react';
import { Calendar, Clock, Video, Ticket, CheckCircle, Loader2, Compass, GraduationCap, Wrench, Footprints, Users, ArrowDown, Flame } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EVENT_ID = 'session-oct-17-2026';
const EVENT_TITLE = 'What After Graduation?';

const EVENT_FACTS = [
  { icon: Calendar, label: 'Date', value: 'To be confirmed soon', testid: 'event-date' },
  { icon: Clock, label: 'Time', value: 'To be confirmed soon', testid: 'event-time' },
  { icon: Video, label: 'Online', value: 'Live Interactive Session', testid: 'event-mode' },
  { icon: Ticket, label: 'Free', value: 'Registration', testid: 'event-fee' },
];

const TOPICS = [
  { icon: Compass, title: 'Career Direction', text: 'Explore possible career paths and understand your options.' },
  { icon: GraduationCap, title: 'Job or Higher Studies', text: 'Understand what to weigh up when deciding your next move.' },
  { icon: Wrench, title: 'Skills to Build Now', text: 'Spot the practical skills that make you more career-ready.' },
  { icon: Footprints, title: 'Your First Steps', text: 'Learn how to start working towards your career goals.' },
];

const inputClass = 'w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-shadow bg-white';

const scrollToForm = () => document.getElementById('register')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const SEO = {
  title: 'What After Graduation? | Free Career Session | Elevate Careers',
  'meta[name="description"]': 'Join Elevate Careers for a free interactive career session exploring jobs, higher studies, skills and your next career steps.',
  'meta[property="og:title"]': 'What After Graduation? | Free Career Session | Elevate Careers',
  'meta[property="og:description"]': 'Free interactive career session, date to be confirmed soon. Explore jobs, higher studies, skills and your next steps.',
  'meta[property="og:url"]': 'https://elevatecareersindia.in/session',
  'link[rel="canonical"]': 'https://elevatecareersindia.in/session',
};

const SessionSeo = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = SEO.title;
    const restore = Object.entries(SEO).filter(([k]) => k !== 'title').map(([selector, value]) => {
      const el = document.head.querySelector(selector);
      if (!el) return () => {};
      const attr = el.tagName === 'LINK' ? 'href' : 'content';
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return () => el.setAttribute(attr, prev);
    });
    return () => { document.title = prevTitle; restore.forEach((fn) => fn()); };
  }, []);
  return null;
};

const SessionHero = () => (
  <section className="relative overflow-hidden bg-[#0b1f4d] text-white">
    <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-teal-500/20 blur-3xl" aria-hidden="true" />
    <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-sky-400/20 blur-3xl" aria-hidden="true" />
    <div className="relative max-w-6xl mx-auto px-5 pt-8 pb-16 md:pt-10 md:pb-24">
      <a href="/" className="inline-block bg-white rounded-xl px-3 py-2" data-testid="event-logo-link">
        <img src="/logo-elevate-careers.webp" alt="Elevate Careers" className="h-8 w-auto" />
      </a>
      <div className="mt-10 md:mt-14 max-w-3xl">
        <span className="inline-flex items-center gap-2 bg-teal-400/15 text-teal-200 border border-teal-300/30 px-4 py-1.5 rounded-full text-sm font-medium">
          <Users className="w-4 h-4" /> A Free Interactive Career Session
        </span>
        <h1 data-testid="event-title" className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          What After <span className="text-teal-300">Graduation?</span>
        </h1>
        <p className="mt-5 text-base md:text-lg text-sky-100/90 leading-relaxed">
          Confused about jobs, higher studies or which skills to focus on?
          <br className="hidden sm:block" /> Join Elevate Careers for a free interactive session to explore your career options, ask questions and understand your next steps.
        </p>
        <p className="mt-3 text-sm text-sky-200/80">For final-year students, graduates and freshers</p>
      </div>
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl">
        {EVENT_FACTS.map(({ icon: Icon, label, value, testid }) => (
          <div key={label} data-testid={testid} className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-4">
            <Icon className="w-5 h-5 text-teal-300" />
            <p className="mt-2 font-semibold">{label}</p>
            <p className="text-sm text-sky-100/80">{value}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <button onClick={scrollToForm} data-testid="event-hero-cta"
          className="inline-flex items-center gap-2 bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] font-semibold px-7 py-4 rounded-full transition-colors">
          Register Now — It's Free <ArrowDown className="w-4 h-4" />
        </button>
        <span data-testid="event-limited-seats" className="inline-flex items-center gap-1.5 text-sm text-amber-200">
          <Flame className="w-4 h-4" /> Limited seats available
        </span>
      </div>
    </div>
  </section>
);

const TopicCards = () => (
  <section className="max-w-6xl mx-auto px-5 py-14 md:py-20">
    <h2 className="text-2xl md:text-3xl font-bold text-[#0b1f4d]" style={{ fontFamily: 'Outfit, sans-serif' }}>What We'll Cover</h2>
    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TOPICS.map(({ icon: Icon, title, text }, i) => (
        <div key={title} data-testid={`event-topic-${i}`}
          className="group bg-white rounded-2xl p-6 border border-sky-100 shadow-sm hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow] duration-300">
          <div className="w-12 h-12 rounded-xl bg-sky-50 group-hover:bg-teal-50 flex items-center justify-center transition-colors">
            <Icon className="w-6 h-6 text-teal-600" />
          </div>
          <h3 className="mt-4 font-semibold text-[#0b1f4d]">{title}</h3>
          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{text}</p>
        </div>
      ))}
    </div>
  </section>
);

const CommunityConsent = ({ checked, onChange }) => (
  <div data-testid="event-community-section" className="rounded-2xl border border-teal-100 bg-teal-50/60 p-4">
    <h3 className="text-sm font-semibold text-[#0b1f4d]">Stay Connected with Elevate Careers</h3>
    <label className="mt-2 flex items-start gap-3 cursor-pointer">
      <input type="checkbox" name="community_opt_in" checked={checked} onChange={onChange}
        className="mt-1 w-4 h-4 text-teal-600 border-slate-300 rounded focus:ring-teal-500" data-testid="event-community-checkbox" />
      <span className="text-sm text-slate-700">
        I'd like to receive an invitation to the Elevate Careers WhatsApp Community for career opportunities, practical resources, upcoming sessions and student discussions.
      </span>
    </label>
    <p className="mt-2 ml-7 text-xs text-slate-500">
      Optional. You can leave the community anytime. Your phone number and profile information may be visible to members of WhatsApp groups you join.
    </p>
  </div>
);

const SuccessScreen = ({ communityOptIn }) => (
  <div className="min-h-screen bg-gradient-to-br from-sky-50 to-teal-50 flex items-center justify-center p-4">
    <SessionSeo />
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
      <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-8 h-8 text-teal-600" />
      </div>
      <h1 data-testid="event-registered-heading" className="text-2xl font-bold text-[#0b1f4d] mb-2">You're Registered! 🎉</h1>
      <p className="text-slate-600 mb-6">Thank you for registering for <strong>{EVENT_TITLE}</strong></p>
      <div className="bg-sky-50 rounded-xl p-4 text-left space-y-3 text-slate-700">
        <p data-testid="event-success-date">📅 Date &amp; time: to be confirmed soon</p>
        <p>💻 Online</p>
      </div>
      <p data-testid="event-success-promise" className="mt-6 text-slate-700">We'll email you the date and time once confirmed, along with the joining link.</p>
      {communityOptIn && (
        <p data-testid="event-community-thanks" className="mt-4 text-sm text-teal-800 bg-teal-50 rounded-xl p-3">
          Thanks for your interest in our student community! We'll share information on how you can join.
        </p>
      )}
      <p className="text-sm font-medium text-slate-700 mt-6">— Team Elevate Careers</p>
      <a href="/" className="inline-block mt-6 text-teal-700 hover:text-teal-800 font-medium" data-testid="event-back-home">← Back to Home</a>
    </div>
  </div>
);

const EventRegistration = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    whatsapp: '',
    institution: '',
    current_status: '',
    education_mode: '',
    main_concern: '',
    question: '',
    university_support: '',
    career_lab_optin: false,
    whatsapp_consent: false,
    community_opt_in: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [error, setError] = useState('');

  const statusOptions = [
    'Pursuing undergraduate degree',
    'Pursuing postgraduate degree',
    'Recent graduate / fresher',
    'Working professional',
    'Other'
  ];

  const educationModeOptions = [
    'Online degree program',
    'Distance learning',
    'Regular / on-campus program',
    'Already graduated / working',
    'Other'
  ];

  const concernOptions = [
    'Unsure about my career direction',
    'Finding internships or job opportunities',
    'Lack of practical experience',
    'Improving my CV or LinkedIn profile',
    'Preparing for interviews',
    'Other'
  ];

  const universitySupportOptions = [
    'Placement or internship assistance',
    'Career guidance or mentoring',
    'Both',
    'No formal support that I\'m aware of',
    'I\'m not sure what\'s available'
  ];

  const showUniversitySupport = formData.education_mode === 'Online degree program' || formData.education_mode === 'Distance learning';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.full_name.trim()) {
      setError('Please enter your full name');
      return;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!formData.whatsapp.trim() || formData.whatsapp.length < 10) {
      setError('Please enter a valid WhatsApp number');
      return;
    }
    if (!formData.current_status) {
      setError('Please select your current status');
      return;
    }
    if (!formData.education_mode) {
      setError('Please select how you are currently studying');
      return;
    }
    if (!formData.main_concern) {
      setError('Please select your main career concern');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/event/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, event_id: EVENT_ID })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      setIsRegistered(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isRegistered) return <SuccessScreen communityOptIn={formData.community_opt_in} />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/60 to-white">
      <SessionSeo />
      <SessionHero />
      <TopicCards />

      <section id="register" className="max-w-3xl mx-auto px-5 pb-16 scroll-mt-6">
        <div className="bg-white rounded-3xl shadow-lg border border-sky-100 p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
            <h2 className="text-xl font-semibold text-[#0b1f4d]">Register for Free</h2>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
              <Flame className="w-3.5 h-3.5" /> Limited seats available
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input type="text" name="full_name" value={formData.full_name} onChange={handleChange}
                  placeholder="Enter your full name" className={inputClass} data-testid="event-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input type="email" name="email" value={formData.email} onChange={handleChange}
                  placeholder="Enter your email" className={inputClass} data-testid="event-email-input" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  WhatsApp Number <span className="text-red-500">*</span>
                </label>
                <input type="tel" name="whatsapp" value={formData.whatsapp} onChange={handleChange}
                  placeholder="Enter your WhatsApp number" className={inputClass} data-testid="event-whatsapp-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">University / Institution</label>
                <input type="text" name="institution" value={formData.institution} onChange={handleChange}
                  placeholder="Optional" className={inputClass} data-testid="event-institution-input" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  What best describes your current status? <span className="text-red-500">*</span>
                </label>
                <select name="current_status" value={formData.current_status} onChange={handleChange}
                  className={inputClass} data-testid="event-status-select">
                  <option value="">Select your status</option>
                  {statusOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  How are you currently studying? <span className="text-red-500">*</span>
                </label>
                <select name="education_mode" value={formData.education_mode} onChange={handleChange}
                  className={inputClass} data-testid="event-education-select">
                  <option value="">Select your education mode</option>
                  {educationModeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                What's your biggest career concern right now? <span className="text-red-500">*</span>
              </label>
              <select name="main_concern" value={formData.main_concern} onChange={handleChange}
                className={inputClass} data-testid="event-concern-select">
                <option value="">Select your main concern</option>
                {concernOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                If you could ask us one career-related question during the session, what would it be?
              </label>
              <textarea name="question" value={formData.question} onChange={handleChange}
                placeholder="For example: Should I start working or go for a master's degree first?"
                maxLength={500} rows={3} className={`${inputClass} resize-none`} data-testid="event-question-input" />
              <p className="text-xs text-slate-400 mt-1">{formData.question.length}/500 characters</p>
            </div>

            {showUniversitySupport && (
              <div className="bg-sky-50 rounded-xl p-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  What kind of career support is available through your university?
                </label>
                <select name="university_support" value={formData.university_support} onChange={handleChange}
                  className={inputClass} data-testid="event-support-select">
                  <option value="">Select an option (optional)</option>
                  {universitySupportOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </div>
            )}

            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" name="whatsapp_consent" checked={formData.whatsapp_consent} onChange={handleChange}
                  className="mt-1 w-4 h-4 text-teal-600 border-slate-300 rounded focus:ring-teal-500" data-testid="event-consent-checkbox" />
                <span className="text-sm text-slate-600">
                  I agree to receive session updates and relevant career-related follow-ups from Elevate Careers on WhatsApp.
                </span>
              </label>
            </div>

            <CommunityConsent checked={formData.community_opt_in} onChange={handleChange} />

            {error && (
              <div data-testid="event-form-error" className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>
            )}

            <button type="submit" disabled={isSubmitting} data-testid="event-submit-btn"
              className="w-full py-4 bg-[#0b1f4d] hover:bg-[#13306f] text-white font-semibold rounded-full transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Registering...</>) : "Register Now — It's Free"}
            </button>
          </form>
        </div>

        <div className="text-center mt-8 text-sm text-slate-500">
          By Elevate Careers • <a href="/" className="text-teal-700 hover:underline">elevatecareersindia.in</a>
        </div>
      </section>
    </div>
  );
};

export default EventRegistration;
